import { createFileRoute, useNavigate, Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Building2, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/medewerker/login")({
  head: () => ({
    meta: [
      { title: "Medewerker inloggen — EVG Office" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): { error?: string; redirect?: string } => ({
    error: typeof s.error === "string" ? s.error : undefined,
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  component: MedewerkerLogin,
});

function MedewerkerLogin() {
  const nav = useNavigate();
  const search = useSearch({ from: "/medewerker/login" });
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(
    search.error === "no_access" ? "Uw account heeft geen toegang tot het medewerkersportaal." : null,
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    setSubmitting(false);
    if (error) {
      setErr("Inloggen mislukt. Controleer e-mail en wachtwoord.");
      return;
    }
    nav({ to: search.redirect ?? "/medewerker/office" });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050d1a] via-[#0b2a4a] to-[#123b6b] text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
            <Building2 className="size-5 text-[#7dd3fc]" />
          </div>
          <div>
            <div className="text-lg font-bold tracking-tight">EVG Office</div>
            <div className="text-xs uppercase tracking-widest text-white/60">Medewerkersportaal</div>
          </div>
        </Link>

        <div className="rounded-2xl bg-white/5 p-8 shadow-2xl ring-1 ring-white/10 backdrop-blur">
          <h1 className="text-2xl font-bold">Inloggen</h1>
          <p className="mt-1 text-sm text-white/60">
            Toegang uitsluitend voor medewerkers van Elvion Group Nederland.
          </p>

          {err && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-500/15 p-3 text-sm text-red-200 ring-1 ring-red-500/30">
              <ShieldAlert className="mt-0.5 size-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-white/70">E-mail</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#7dd3fc]/50 focus:bg-white/10 focus:outline-none"
                placeholder="jij@elvion-group.nl"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-white/70">Wachtwoord</label>
              <input
                type="password"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#7dd3fc]/50 focus:bg-white/10 focus:outline-none"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#3a7bd5] hover:bg-[#4d90e8] text-white font-semibold py-5"
            >
              {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Inloggen
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-white/40">
            Geen medewerker?{" "}
            <Link to="/auth" className="text-[#7dd3fc] hover:underline">Klant-inloggen</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
